import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, Award as AwardIcon, Medal, Star, Trophy, Shield, Heart, Crown, Flame,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "boe_admin_token";
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

const ICONS = { medal: Medal, star: Star, trophy: Trophy, shield: Shield, heart: Heart, crown: Crown, flame: Flame, award: AwardIcon };
const ICON_KEYS = Object.keys(ICONS);
const fieldClass = "h-11 rounded-xl bg-white/5 border-white/15 text-cream placeholder:text-cream/40 focus-visible:ring-gold/40 focus-visible:border-gold/60";

const AwardsPanel = ({ onLogout }) => {
  const [users, setUsers] = useState(null);
  const [awards, setAwards] = useState([]);
  const [dialogUser, setDialogUser] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", icon: "medal" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [u, a] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers: authHeader() }),
        axios.get(`${API}/admin/awards`, { headers: authHeader() }),
      ]);
      setUsers(u.data);
      setAwards(a.data);
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error("Daten konnten nicht geladen werden.");
      setUsers([]);
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const openGive = (user) => { setDialogUser(user); setForm({ title: "", description: "", icon: "medal" }); };

  const give = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/awards`, {
        user_id: dialogUser.id, title: form.title.trim(), description: form.description.trim() || null, icon: form.icon,
      }, { headers: authHeader() });
      setAwards((prev) => [data, ...prev]);
      toast.success(`Auszeichnung an ${dialogUser.name} vergeben.`);
      setDialogUser(null);
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error("Vergabe fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/awards/${id}`, { headers: authHeader() });
      setAwards((prev) => prev.filter((a) => a.id !== id));
      toast.success("Auszeichnung entfernt.");
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error("Entfernen fehlgeschlagen.");
    }
  };

  if (users === null) return <div className="flex items-center justify-center py-24 text-cream/60"><Loader2 size={28} className="animate-spin" /></div>;

  return (
    <div data-testid="awards-panel">
      <div className="mb-8">
        <span className="section-eyebrow">Ehrungen</span>
        <h1 className="mt-3 text-3xl md:text-4xl font-semibold text-cream tracking-tight">Auszeichnungen</h1>
        <p className="mt-2 text-sm text-cream/60">Medaillen an Mitglieder vergeben – sie erscheinen im Profil.</p>
      </div>

      {users.length === 0 ? (
        <p className="text-sm text-cream/45 py-12 text-center border border-dashed border-white/10 rounded-2xl" data-testid="awards-empty">
          Noch keine Benutzer. Erstelle zuerst im Tab „Benutzer" ein Konto.
        </p>
      ) : (
        <div className="grid gap-5 md:grid-cols-2" data-testid="awards-users">
          {users.map((u) => {
            const userAwards = awards.filter((a) => a.user_id === u.id);
            return (
              <div key={u.id} data-testid={`awards-user-${u.id}`} className="rounded-2xl border border-gold/15 bg-white/[0.03] p-5">
                <div className="flex items-center justify-between gap-3 pb-4 mb-4 border-b border-gold/15">
                  <div>
                    <p className="font-semibold text-cream">{u.name}</p>
                    <p className="text-xs text-cream/50">{u.rank} · {u.region}</p>
                  </div>
                  <button onClick={() => openGive(u)} data-testid={`awards-give-${u.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full border border-gold/40 px-3 py-1.5 hover:bg-gold hover:text-anthracite transition-colors" style={{ color: "#E5C76B" }}>
                    <Plus size={14} /> Vergeben
                  </button>
                </div>
                {userAwards.length === 0 ? (
                  <p className="text-sm text-cream/40 py-2">Noch keine Auszeichnungen.</p>
                ) : (
                  <div className="space-y-2.5">
                    {userAwards.map((a) => {
                      const Icon = ICONS[a.icon] || Medal;
                      return (
                        <div key={a.id} data-testid={`award-${a.id}`} className="flex items-start gap-3 rounded-xl border border-gold/20 bg-gold/[0.06] px-3 py-2.5">
                          <div className="w-9 h-9 rounded-full border border-gold/40 flex items-center justify-center flex-shrink-0" style={{ background: "rgba(198,156,34,0.12)" }}>
                            <Icon size={17} style={{ color: "#E5C76B" }} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-cream">{a.title}</p>
                            {a.description && <p className="text-xs text-cream/55">{a.description}</p>}
                          </div>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button data-testid={`award-delete-${a.id}`} aria-label="Entfernen" className="text-cream/40 hover:text-red-400 transition-colors flex-shrink-0"><Trash2 size={15} /></button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-anthracite-soft border border-gold/20 text-cream">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="text-cream">Auszeichnung entfernen?</AlertDialogTitle>
                                <AlertDialogDescription className="text-cream/60">„{a.title}" von {u.name} wirklich entfernen?</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="bg-transparent border-white/20 text-cream hover:bg-white/10 hover:text-cream">Abbrechen</AlertDialogCancel>
                                <AlertDialogAction data-testid={`award-delete-confirm-${a.id}`} onClick={() => remove(a.id)} className="bg-red-600 text-white hover:bg-red-700">Entfernen</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!dialogUser} onOpenChange={(o) => !o && setDialogUser(null)}>
        <DialogContent className="bg-anthracite-soft border border-gold/20 text-cream sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-cream flex items-center gap-2">
              <AwardIcon size={18} style={{ color: "#E5C76B" }} /> Auszeichnung an {dialogUser?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={give} className="space-y-4 mt-2" data-testid="award-form">
            <label className="flex flex-col gap-1.5">
              <span className="text-xs uppercase tracking-[0.18em] font-semibold text-cream/55">Titel</span>
              <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} required data-testid="award-form-title" className={fieldClass} placeholder="z.B. Bester Helfer 2026" />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs uppercase tracking-[0.18em] font-semibold text-cream/55">Beschreibung (optional)</span>
              <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2} data-testid="award-form-description" className="rounded-xl bg-white/5 border-white/15 text-cream placeholder:text-cream/40 focus-visible:ring-gold/40 focus-visible:border-gold/60 resize-none" />
            </label>
            <div className="flex flex-col gap-2">
              <span className="text-xs uppercase tracking-[0.18em] font-semibold text-cream/55">Symbol</span>
              <div className="grid grid-cols-8 gap-2" data-testid="award-form-icons">
                {ICON_KEYS.map((key) => {
                  const Icon = ICONS[key];
                  const active = form.icon === key;
                  return (
                    <button key={key} type="button" onClick={() => setForm((f) => ({ ...f, icon: key }))} data-testid={`award-icon-${key}`}
                      className={`aspect-square rounded-lg border flex items-center justify-center transition-colors ${active ? "border-gold bg-gold/20" : "border-white/15 hover:border-gold/40"}`}>
                      <Icon size={18} style={{ color: active ? "#E5C76B" : "rgba(250,250,248,0.6)" }} />
                    </button>
                  );
                })}
              </div>
            </div>
            <DialogFooter className="mt-2">
              <button type="submit" disabled={saving} data-testid="award-form-submit" className="btn-primary w-full disabled:opacity-70">
                {saving ? <><Loader2 size={16} className="animate-spin" /> Vergeben…</> : "Auszeichnung vergeben"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AwardsPanel;
